import { CreateOrgRequest, OrganizationService } from '@/api';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { file2Base64 } from '@/utils/file2Base64';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const formSchema = z.object({
  orgName: z.string().min(2, {
    message: 'Organization name must be at least 2 characters.',
  }),
  orgLogo: z.instanceof(File).optional().nullable(),
  orgAreaCode: z.string().optional().default(''),
});

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function NewOrganizationDrawer({ open, setOpen }: Props) {
  // query client
  const queryClient = useQueryClient();

  // Define the form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      orgName: '',
      orgAreaCode: '',
    },
  });

  // mutation to create a new organization
  const createOrg = useMutation({
    mutationFn: async (payload: CreateOrgRequest) => {
      // call the api to create the organization
      const res = await OrganizationService.createOrganization(payload);
      return res;
    },
    onSuccess: data => {
      // invalidate queries here so that they are refetched
      queryClient.invalidateQueries({
        queryKey: ['getOrganizations'],
      });

      // close the drawer
      setOpen(false);
      toast.success('Organization created successfully');
    },
    onError: error => {
      console.error(error);
      setOpen(false);
      toast('An error occurred', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // Function to generate initials as an image using canvas
  const generateInitialsImage = (initials: string): string => {
    console.log(`Generating fallback image for: ${initials}`);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Set canvas size
    canvas.width = 100;
    canvas.height = 100;

    // Background color
    context!.fillStyle = '#F0F0F0';
    context!.fillRect(0, 0, canvas.width, canvas.height);

    // Text properties
    context!.font = 'bold 40px Arial';
    context!.fillStyle = '#333333';
    context!.textAlign = 'center';
    context!.textBaseline = 'middle';

    // Draw initials
    context!.fillText(initials, canvas.width / 2, canvas.height / 2);

    // Get the base64 image data and remove the prefix
    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl.replace('data:image/png;base64,', '');
  };

  // Handle form submission
  async function onSubmit(values: z.infer<typeof formSchema>) {
    const payload: CreateOrgRequest = {
      name: values.orgName,
      areaCode: values.orgAreaCode,
    };

    if (values.orgLogo) {
      // Convert the uploaded logo file to base64 if provided
      const logoBase64 = await file2Base64(values.orgLogo);
      payload.logoBase64 = logoBase64;
      payload.logoFileName = values.orgLogo?.name;
    } else {
      // Generate a fallback image (initials) if no logo is provided
      const initials = values.orgName.slice(0, 2).toUpperCase().padEnd(2, 'X');
      const fallbackImage = generateInitialsImage(initials);
      payload.logoBase64 = fallbackImage;
      payload.logoFileName = `${initials}_initials.png`;
    }

    // Call the mutation to create the organization
    createOrg.mutate(payload);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={o => {
        form.reset();
        setOpen(o);
      }}
    >
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Create new Organization</SheetTitle>
          <SheetDescription>
            An organization is a billing container for projects and
            collaborators. Create an organization to get started.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 space-y-8"
          >
            <FormField
              control={form.control}
              name="orgName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Organization name</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Inc" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the name for the organization.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="orgAreaCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Area Code (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="415" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the area code for the organization's phone number.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="orgLogo"
              render={({ field: { onChange }, ...field }) => (
                <FormItem>
                  <FormLabel>Organization logo</FormLabel>
                  <FormControl>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple={false}
                      {...field}
                      onChange={event =>
                        onChange(
                          event.target.files ? event.target.files[0] : null,
                        )
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    Upload a logo for the organization. (this is an optional field)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Cancel</Button>
              </SheetClose>
              <Button type="submit" disabled={createOrg.isPending}>
                {createOrg.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Submit
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
